'use client';

import { Box, IconButton, type Theme, useTheme } from '@mui/material';
import MUIModal from '@mui/material/Modal';
import { FaWindowClose } from 'react-icons/fa';
import { type Ref, type ReactNode, type UIEvent, useEffect } from 'react';

export const useModalSx = (theme: Theme) => ({
	modal: {
		top: '50%',
		left: '50%',
		transform: `translate(-50%, -50%)`,
		width: '50%',
		maxHeight: '80%',
		[theme.breakpoints.down('sm')]: {
			width: '90%',
			maxHeight: '90%',
		},
	},
	paper: {
		position: 'absolute',
		width: '50%',
		height: 'auto',
		backgroundColor: 'white',
		border: 'none',
		padding: theme.spacing(5, 5, 5),
		overflowY: 'auto',
		color: 'black',
		[theme.breakpoints.down('sm')]: {
			width: '90%',
			maxHeight: '90%',
		},
	},
	closeButton: {
		position: 'absolute',
		right: theme.spacing(0),
		top: theme.spacing(0),
		color: 'black',
		padding: 5,
	},
});

export interface ModalProps {
	children?: ReactNode;
	open: boolean;
	handleClose: () => void;
	callbackRef?: () => void;
	modalRef?: Ref<HTMLDivElement>;
	handleScroll?: (event: UIEvent<HTMLDivElement>) => void;
}

export const Modal = ({ open, handleClose, children, callbackRef, modalRef, handleScroll }: ModalProps) => {
	const theme = useTheme();
	const sx = useModalSx(theme);

	useEffect(() => {
		const onEscapePressed = (e: KeyboardEvent) => e.key === 'Escape' && handleClose();
		window.addEventListener('keydown', onEscapePressed);

		return () => {
			window.removeEventListener('keydown', onEscapePressed);
		};
	}, [handleClose]);

	if (!open) return null;

	return (
		<MUIModal disableEnforceFocus disableAutoFocus open={open} onClose={handleClose} ref={callbackRef}>
			<Box sx={{ ...sx.paper, ...sx.modal }} ref={modalRef} onScroll={handleScroll}>
				<IconButton aria-label="close" sx={sx.closeButton} onClick={handleClose} size="large">
					<FaWindowClose />
				</IconButton>
				{children}
			</Box>
		</MUIModal>
	);
};
